import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';

interface EmotionDataPoint {
  date: string;
  displayDate: string;
  alignment: number;
  emotion: string;
  count: number;
}

interface EmotionFlowChartProps {
  data: EmotionDataPoint[];
  height?: number;
  showGrid?: boolean;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-4 shadow-2xl border border-black/5">
        <p className="text-[11px] font-bold text-black/40 uppercase tracking-wider mb-1">
          {data.displayDate}
        </p>
        <p className="text-[24px] font-black text-black leading-none mb-2">
          {data.alignment}%
        </p>
        <div className="flex items-center gap-2">
          <span className="text-lg">{data.emotion}</span>
          <span className="text-[12px] text-black/50">{data.count} momenti</span>
        </div>
      </div>
    );
  }
  return null;
};

const EmotionFlowChart: React.FC<EmotionFlowChartProps> = ({
  data,
  height = 200,
  showGrid = false
}) => {
  const chartData = useMemo(() => {
    if (data.length === 0) return [];
    return data.slice(-14);
  }, [data]);

  if (chartData.length < 2) {
    return (
      <div className="flex items-center justify-center h-[200px] bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl">
        <p className="text-black/30 text-sm font-medium">Ancora pochi dati per mostrare il flusso</p>
      </div>
    );
  }

  return (
    <div className="w-full emotion-flow-chart">
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={chartData}
          margin={{ top: 20, right: 10, left: -20, bottom: 0 }}
        >
          <defs>
            <linearGradient id="emotionGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
              <stop offset="50%" stopColor="#8b5cf6" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="50%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#a855f7" />
            </linearGradient>
          </defs>
          
          {showGrid && (
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="rgba(0,0,0,0.05)" 
              vertical={false}
            />
          )}
          
          <XAxis
            dataKey="displayDate"
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'rgba(0,0,0,0.3)', fontSize: 10, fontWeight: 600 }}
            dy={10}
          />
          
          <YAxis
            domain={[0, 100]}
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'rgba(0,0,0,0.3)', fontSize: 10, fontWeight: 600 }}
            width={40}
          />
          
          <Tooltip content={<CustomTooltip />} cursor={false} />
          
          <Area
            type="monotone"
            dataKey="alignment"
            stroke="url(#lineGradient)"
            strokeWidth={3}
            fill="url(#emotionGradient)"
            animationDuration={1500}
            animationEasing="ease-out"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default EmotionFlowChart;
